import axios from 'axios';
import { Question, SurveyRecord, CreateSurveyDto, Dimension } from '../types';

const API_BASE = process.env.REACT_APP_API_BASE || '';

const api = axios.create({
  baseURL: API_BASE,
});

// Inject auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 — clear token
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Let the app handle redirect via AuthContext
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
    return Promise.reject(err);
  },
);

// ─── Auth ───

export const login = (username: string, password: string) =>
  api.post<{ user: any; token: string }>('/api/auth/login', { username, password }).then(r => r.data);

export const register = (username: string, password: string, displayName?: string) =>
  api.post<{ user: any; token: string }>('/api/auth/register', { username, password, displayName }).then(r => r.data);

export const getMe = () =>
  api.get<{ user: any; token: string }>('/api/auth/me').then(r => r.data);

// ─── Surveys ───

export const getQuestions = () =>
  api.get<Question[]>('/api/surveys/questions').then(r => r.data);

export const getDimensions = () =>
  api.get<Dimension[]>('/api/dimensions').then(r => r.data);

export const getSurveys = () =>
  api.get<SurveyRecord[]>('/api/surveys').then(r => r.data);

export const getLatestSurvey = () =>
  api.get<SurveyRecord | null>('/api/surveys/latest').then(r => r.data);

export const getSurvey = (id: string) =>
  api.get<SurveyRecord>(`/api/surveys/${id}`).then(r => r.data);

export const createSurvey = (dto: CreateSurveyDto) =>
  api.post<SurveyRecord>('/api/surveys', dto).then(r => r.data);

export const deleteSurvey = (id: string) =>
  api.delete(`/api/surveys/${id}`).then(r => r.data);

export const compareSurveys = (ids: string[]) =>
  api.get<SurveyRecord[]>('/api/surveys/compare/list', { params: { ids: ids.join(',') } }).then(r => r.data);

// ─── Admin ───

export const adminGetUsers = () =>
  api.get<{ users: any[]; stats: Record<string, any> }>('/api/surveys/admin/users').then(r => r.data);

export const adminGetAllSurveys = () =>
  api.get<SurveyRecord[]>('/api/surveys/admin/all').then(r => r.data);

export const adminGetUserSurveys = (userId: string) =>
  api.get<{ user: any; records: SurveyRecord[] }>(`/api/surveys/admin/user/${userId}`).then(r => r.data);

// ─── Questionnaires (问卷模板管理) ───

export const getQuestionnaires = () =>
  api.get<any[]>('/api/questionnaires').then(r => r.data);

export const getActiveQuestionnaires = () =>
  api.get<any[]>('/api/questionnaires/active').then(r => r.data);

export const getQuestionnaire = (id: string) =>
  api.get<any>(`/api/questionnaires/${id}`).then(r => r.data);

export const createQuestionnaire = (dto: any) =>
  api.post<any>('/api/questionnaires', dto).then(r => r.data);

export const updateQuestionnaire = (id: string, dto: any) =>
  api.put<any>(`/api/questionnaires/${id}`, dto).then(r => r.data);

export const deleteQuestionnaire = (id: string) =>
  api.delete(`/api/questionnaires/${id}`).then(r => r.data);
